import { useEffect, useMemo } from 'react';
import { usePropertyContext, useRoleContext } from '@/shared/providers';
import * as Library from '@pkg/entities/library';
import * as Snapshots from '@pkg/entities/snapshots';
import makeGraph from '@pkg/entities/snapshots/graph/makeGraph';
import useGraphDispatcher from '@pkg/entities/snapshots/useGraphDispatcher';
import useGraphState from '@pkg/entities/snapshots/useGraphState';
import useReduced from '@pkg/entities/snapshots/utils/useReduced';
import { ClientError } from '@/lib/enums';
import useDesignStore from './useDesignStore';
import useMutate from './useMutate';

/**
 * @param {Object} snapshot
 * @param {Object} level
 * @returns {Object|ClientError}
 */
export default function useSnapshot() {
  const library = Library.useLibrary();
  const propertyContext = usePropertyContext();
  const roleContext = useRoleContext();

  const access = useDesignStore((state) => state.access);
  const conditions = useDesignStore((state) => state.conditions);
  const designId = useDesignStore((state) => state.design?.uuid);
  const isLoading = useDesignStore((state) => state.isLoading);
  const level = useDesignStore((state) => state.level);
  const set = useDesignStore((state) => state.set);
  const settings = useDesignStore((state) => state.settings);
  const setActions = useDesignStore((state) => state.setActions);
  const snapshot = useDesignStore((state) => state.revision?.snapshot);
  const isHistorical = useDesignStore((state) => state.isHistorical);

  /**
   * SNAPSHOTS
   */

  const [working, setWorking] = useGraphState({
    snapshot,
    activities: settings?.activities?.state,
    conditions,
    library,
    isHistorical,
  });

  const reduced = useReduced(working, level);
  const hasError = Object.values(ClientError).includes(reduced);
  const state = useMemo(() => {
    if (!reduced || hasError) return null;
    if (reduced === working) return working;
    return makeGraph(reduced, {
      activities: settings?.activities?.state,
      conditions,
      library,
    });
  }, [
    reduced,
    working,
    hasError,
    settings?.activities?.state,
    conditions,
    library,
  ]);

  const { emit, flush } = useMutate({ designId, state: working });
  const dispatch = useGraphDispatcher({
    state: working,
    setState: setWorking,
    onMutate: emit,
  });

  // update snapshot in the store
  useEffect(() => {
    if (!access.isReady || isLoading) {
      set({ working: null, snapshot: null, isReady: false });
      return;
    }

    if (hasError) {
      set({
        error: reduced,
        isError: true,
        working,
        snapshot: null,
        isReady: true,
      });
      return;
    }

    set({ working, snapshot: state, isReady: Boolean(state) });
  }, [access?.isReady, isLoading, hasError, reduced, working, state]);

  /**
   * MISC
   */

  // bind dispatch action
  useEffect(() => {
    setActions({ dispatch, flush });
  }, [dispatch, flush]);

  // update context indexes
  useEffect(() => {
    if (!state) return;

    if (propertyContext?.updatePropertyIndexes) {
      console.time('DesignContainer.useSnapshot.propertyMap');
      const propertyMap = Snapshots.derivePropertyMap(state);
      propertyContext?.updatePropertyIndexes(propertyMap);
      console.timeEnd('DesignContainer.useSnapshot.propertyMap');
    }

    if (roleContext?.updateRoleIndexes) {
      console.time('DesignContainer.useSnapshot.roleMap');
      roleContext?.updateRoleIndexes(state.entities.roles);
      console.timeEnd('DesignContainer.useSnapshot.roleMap');
    }
  }, [state]);
}
